import { useState } from 'react'
import { Copy, Check, Lock } from 'lucide-react'
import { Task } from '@renderer/types/api'

interface TasksSidebarProps {
  tasks: Task[]
  completedTaskIds: number[]
  unverifiedTaskIds?: number[]
  currentTaskId?: number
  onSelectTask: (task: Task) => void
  moduleTitle?: string
  exerciseTitle?: string
  exerciseRefCode?: string
  // Optional terminal command hint shown in the footer
  // (e.g., { label: 'Run on cluster3', command: 'ssh admin@192.168.0.103' })
  terminalHint?: { label: string; command: string } | null
  currentStep?: number
  totalSteps?: number
  sessionActive?: boolean
  noticeText?: string | null
  teachingMode?: 'guide' | 'assist'
  onTeachingModeChange?: (mode: 'guide' | 'assist') => void
}

export const TasksSidebar: React.FC<TasksSidebarProps> = ({
  tasks,
  completedTaskIds,
  unverifiedTaskIds = [],
  noticeText,
  currentTaskId,
  onSelectTask,
  moduleTitle,
  exerciseTitle,
  exerciseRefCode,
  terminalHint,
  sessionActive = false,
  teachingMode,
  onTeachingModeChange
}) => {
  const completed = completedTaskIds.length
  const total = tasks.length
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0

  // Derive the eyebrow: "M2.E1 · CLUSTER SETUP"
  const eyebrowParts: string[] = []
  if (exerciseRefCode) eyebrowParts.push(exerciseRefCode)
  if (moduleTitle) eyebrowParts.push(moduleTitle.toUpperCase())

  return (
    <div className="h-full flex flex-col" style={{ background: 'inherit' }}>
      {/* Header */}
      <div
        className="shrink-0 px-5 pt-5 pb-3 border-b"
        style={{ borderColor: '#f0e6cd' }}
      >
        {eyebrowParts.length > 0 && (
          <p
            className="text-[11px] font-semibold tracking-[0.12em] uppercase mb-1.5"
            style={{ color: '#b8923a' }}
          >
            {eyebrowParts.join(' · ')}
          </p>
        )}
        {exerciseTitle && (
          <h2
            className="text-[16px] font-bold leading-snug mb-3"
            style={{ color: '#1d160a', letterSpacing: '-0.01em' }}
          >
            {exerciseTitle}
          </h2>
        )}
        {total > 0 && (
          <div className="flex items-center gap-2.5">
            <div
              className="flex-1 h-1 rounded-full overflow-hidden"
              style={{ background: '#efe5cb' }}
            >
              <div
                className="h-full transition-all duration-500 ease-out"
                style={{
                  width: `${pct}%`,
                  background: 'linear-gradient(90deg, #7a5a1e, #b8923a)'
                }}
              />
            </div>
            <span
              className="text-[12px] font-medium tabular-nums"
              style={{ color: '#4a3f2a' }}
            >
              {completed}/{total}
            </span>
          </div>
        )}

        {teachingMode && onTeachingModeChange && sessionActive && (
          <div className="flex items-center gap-2 mt-3">
            <span className="text-[11px] shrink-0" style={{ color: '#7a6e54' }}>
              Tutor
            </span>
            <div
              className="flex rounded-md border overflow-hidden"
              style={{ borderColor: '#e4d6b0' }}
            >
              {(['guide', 'assist'] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => onTeachingModeChange(m)}
                  aria-pressed={teachingMode === m}
                  title={
                    m === 'guide'
                      ? 'Guide me step by step'
                      : 'Assist me when I need help'
                  }
                  className="px-2.5 py-1 text-[11px] font-semibold capitalize transition-colors"
                  style={{
                    background: teachingMode === m ? '#7a5a1e' : 'transparent',
                    color: teachingMode === m ? '#ffffff' : '#7a6e54'
                  }}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Task list */}
      <div className="flex-1 relative overflow-y-auto px-2 py-2">
        {!sessionActive && tasks.length > 0 && (
          <div
            className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 rounded-md"
            style={{ background: 'rgba(250,244,227,0.82)', backdropFilter: 'blur(1px)' }}
          >
            <Lock className="w-4 h-4" style={{ color: '#b8923a' }} />
            <p className="text-[12px] font-medium text-center px-4" style={{ color: '#7a6e54' }}>
              Start a session to interact with tasks
            </p>
          </div>
        )}
        {noticeText && (
          <div
            className="mb-2 px-3 py-2 rounded-md text-[12px]"
            style={{ background: '#f3e8c8', color: '#7a5a1e' }}
            role="status"
          >
            {noticeText}
          </div>
        )}
        {tasks.length === 0 ? (
          <div
            className="text-center text-[13px] py-8"
            style={{ color: '#a89c7e' }}
          >
            No tasks available
          </div>
        ) : (
          tasks.map((task) => {
            const isCompleted = completedTaskIds.includes(task.id)
            const isUnverified = isCompleted && unverifiedTaskIds.includes(task.id)
            const isCurrent = currentTaskId === task.id
            const isClickable = !isCurrent && (!isCompleted || isUnverified)
            const state = isCompleted
              ? (isUnverified ? 'done-unverified' : 'done')
              : isCurrent
                ? 'active'
                : 'pending'
            return (
              <TaskRow
                key={task.id}
                task={task}
                state={state}
                onClick={() => isClickable && onSelectTask(task)}
              />
            )
          })
        )}
      </div>

      {/* Terminal hint footer */}
      {terminalHint && <TerminalHint label={terminalHint.label} command={terminalHint.command} />}
    </div>
  )
}

type RowState = 'pending' | 'active' | 'done' | 'done-unverified'

const TaskRow: React.FC<{
  task: Task
  state: RowState
  onClick: () => void
}> = ({ task, state, onClick }) => {
  const isActive = state === 'active'
  const isUnverified = state === 'done-unverified'
  const isDone = state === 'done' || isUnverified
  const hoverable = state === 'pending' || isUnverified

  return (
    <div
      onClick={onClick}
      className="relative pl-10 pr-3 py-3 rounded-md transition-colors"
      style={{
        cursor: hoverable ? 'pointer' : 'default',
        background: isActive ? '#f3e8c8' : 'transparent'
      }}
      title={
        isUnverified
          ? "Marked done from what was on screen — click to go back to it"
          : undefined
      }
      onMouseEnter={(e) => {
        if (hoverable) e.currentTarget.style.background = '#faf4e3'
      }}
      onMouseLeave={(e) => {
        if (hoverable) e.currentTarget.style.background = 'transparent'
      }}
    >
      {/* Active accent rail */}
      {isActive && (
        <div
          className="absolute left-0 top-3.5 bottom-3.5 w-[2px] rounded-full"
          style={{ background: '#b8923a' }}
        />
      )}

      {/* Marker */}
      <Marker state={state} />

      {/* Step code */}
      {task.ref_code && (
        <p
          className="text-[10px] font-semibold tracking-[0.08em] uppercase mb-0.5"
          style={{ color: isActive ? '#b8923a' : '#7a6e54' }}
        >
          {task.ref_code}
        </p>
      )}

      {/* Title */}
      <p
        className="text-[13px] leading-snug"
        style={{
          color: isActive ? '#1d160a' : isDone ? '#7a6e54' : '#4a3f2a',
          fontWeight: isActive ? 600 : 500,
          textDecoration: isDone ? 'line-through' : undefined,
          textDecorationColor: isDone ? '#a89c7e' : undefined
        }}
      >
        {task.title}
      </p>
    </div>
  )
}

const Marker: React.FC<{ state: RowState }> = ({ state }) => {
  if (state === 'done-unverified') {
    return (
      <div
        className="absolute left-3 top-3.5 w-[18px] h-[18px] rounded-full grid place-items-center"
        style={{ background: 'transparent', border: '1.5px solid #7a5a1e' }}
      >
        <Check className="w-[10px] h-[10px]" style={{ color: '#7a5a1e' }} strokeWidth={3} />
      </div>
    )
  }
  if (state === 'done') {
    return (
      <div
        className="absolute left-3 top-3.5 w-[18px] h-[18px] rounded-full grid place-items-center"
        style={{ background: '#7a5a1e' }}
      >
        <Check className="w-[10px] h-[10px] text-white" strokeWidth={3} />
      </div>
    )
  }
  if (state === 'active') {
    return (
      <div
        className="absolute left-3 top-3.5 w-[18px] h-[18px] rounded-full grid place-items-center"
        style={{
          background: '#ffffff',
          border: '1.5px solid #b8923a',
          boxShadow: '0 0 0 3px rgba(184, 146, 58, 0.18)'
        }}
      >
        <div
          className="w-[6px] h-[6px] rounded-full"
          style={{ background: '#b8923a', animation: 'tasks-pulse 1.6s infinite' }}
        />
        <style>{`
          @keyframes tasks-pulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.5; transform: scale(0.85); }
          }
        `}</style>
      </div>
    )
  }
  return (
    <div
      className="absolute left-3 top-3.5 w-[18px] h-[18px] rounded-full"
      style={{ background: '#faf4e3', border: '1.5px solid #e4d6b0' }}
    />
  )
}

const TerminalHint: React.FC<{ label: string; command: string }> = ({ label, command }) => {
  const [copied, setCopied] = useState(false)

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(command)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // ignore
    }
  }

  return (
    <div className="shrink-0 px-3 pb-3 pt-2" style={{ background: 'inherit' }}>
      <div
        className="rounded-md px-3 py-2.5"
        style={{ background: '#1d160a', border: '1px solid #2a1f10' }}
      >
        <div className="flex items-center justify-between mb-1">
          <span
            className="text-[10px] font-semibold tracking-[0.1em] uppercase"
            style={{ color: '#a89c7e' }}
          >
            {label}
          </span>
          <button
            onClick={onCopy}
            className="inline-flex items-center gap-1 text-[10px] transition-colors"
            style={{ color: copied ? '#b8923a' : '#a89c7e' }}
          >
            {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
            {copied ? 'Copied' : 'copy'}
          </button>
        </div>
        <code
          className="block text-[12px] break-all"
          style={{ color: '#e6c870', fontFamily: "'JetBrains Mono', monospace" }}
        >
          {command}
        </code>
      </div>
    </div>
  )
}
